import { useMemo, useState } from 'react';
import {
  Alert,
  FormControlLabel,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { DashboardHappinessSurveyItem } from '@/api/dashboardApi';
import { EmptyStateMessage, Section, SectionCard, SectionHeader, StatusChip } from '@/components/common';
import { formatDateShort } from '@/utils/dateFormatter';
import { selectVisibleSurveys } from './HappinessSurveySection.utils';

const ANONYMITY_NOTICE =
  'This survey is anonymous. Your responses will not influence your Job End or Appraisal process.';

export interface HappinessSurveySectionProps {
  completionRate: number | null;
  surveys: DashboardHappinessSurveyItem[];
}

export function HappinessSurveySection({ completionRate, surveys }: HappinessSurveySectionProps) {
  const [showResolved, setShowResolved] = useState(false);

  const visibleSurveys = useMemo(
    () => selectVisibleSurveys(surveys, showResolved),
    [surveys, showResolved]
  );

  const completionRateText =
    completionRate === null
      ? '6-month completion rate: N/A'
      : `6-month completion rate: ${completionRate}%`;

  return (
    <Section>
      <SectionHeader
        action={
          <FormControlLabel
            control={
              <Switch
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
                size="small"
                sx={{ '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              />
            }
            label="Show completed & expired"
          />
        }
      >
        HAPPINESS SURVEYS
      </SectionHeader>
      <SectionCard>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {completionRateText}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {ANONYMITY_NOTICE}
        </Alert>
        {visibleSurveys.length === 0 ? (
          <EmptyStateMessage message="No happiness surveys to display." />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SURVEY</TableCell>
                  <TableCell sx={{ width: '25%', minWidth: 150 }}>DUE DATE</TableCell>
                  <TableCell sx={{ width: '15%', minWidth: 90 }}>STATUS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleSurveys.map((s) => {
                  const label = `Happiness survey – w/e ${formatDateShort(s.dueDate)}`;
                  return (
                    <TableRow key={s.id}>
                      <TableCell>
                        {s.qualtricsLink ? (
                          <Link
                            href={s.qualtricsLink}
                            underline="hover"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {label}
                          </Link>
                        ) : (
                          label
                        )}
                      </TableCell>
                      <TableCell>{formatDateShort(s.dueDate)}</TableCell>
                      <TableCell>
                        <StatusChip status={s.status} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </Section>
  );
}

export default HappinessSurveySection;
